import type { CouponRecord, Currency } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { appliesToIdsForSubmit, appliesToModeFromLists } from '../../appliesToMode';
import { AppliesToModeFields } from '../../AppliesToModeFields';
import { CampaignDiscountSalesItemTagsPicker } from '../../Campaign/pickers/CampaignDiscountSalesItemTagsPicker';
import { DISCOUNT_KIND } from '../../discountKind';
import { DiscountKindRadioGroup } from '../../DiscountKindRadioGroup';
import {
  DISCOUNT_PERCENTAGE_MAX,
  DISCOUNT_PERCENTAGE_MIN,
  discountAmountInputProps,
  discountPercentageInputProps,
  isValidDiscountAmount,
  isValidDiscountPercentage
} from '../../discountValueInputs';
import { usePromotionBrandScope } from '../../usePromotionBrandScope';
import { invalidateAfterCouponMutation } from '../couponInvalidations';
import { CouponSalesItemsPicker } from '../create/CouponSalesItemsPicker';

const FORM_ID = 'edit-coupon-discount-form';

const validationSchema = z
  .object({
    discountKind: z.enum([DISCOUNT_KIND.amount, DISCOUNT_KIND.percentage]),
    discountAmount: z.number().nullable(),
    discountPercentage: z.number().nullable(),
    appliesToAllProducts: z.boolean(),
    filterByProducts: z.boolean(),
    filterByTags: z.boolean(),
    salesItemIds: z.array(z.number()),
    salesItemTagIds: z.array(z.number())
  })
  .superRefine((value, ctx) => {
    if (value.discountKind === DISCOUNT_KIND.amount && !isValidDiscountAmount(value.discountAmount)) {
      ctx.addIssue({ code: 'custom', message: 'Enter a discount amount', path: ['discountAmount'] });
    }
    if (value.discountKind === DISCOUNT_KIND.percentage && !isValidDiscountPercentage(value.discountPercentage)) {
      ctx.addIssue({
        code: 'custom',
        message: `Enter a percentage between ${DISCOUNT_PERCENTAGE_MIN} and ${DISCOUNT_PERCENTAGE_MAX}`,
        path: ['discountPercentage']
      });
    }
    if (value.appliesToAllProducts) {
      return;
    }
    if (!value.filterByProducts && !value.filterByTags) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pick products, tags, or all products',
        path: ['filterByProducts']
      });
      return;
    }
    if (value.filterByProducts && value.salesItemIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pick at least one product',
        path: ['salesItemIds']
      });
    }
    if (value.filterByTags && value.salesItemTagIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pick at least one tag',
        path: ['salesItemTagIds']
      });
    }
  });

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  coupon: CouponRecord;
};

const getDefaultValues = (coupon: CouponRecord): FormValues => ({
  discountKind: coupon.discount && coupon.discount.amount > 0 ? DISCOUNT_KIND.amount : DISCOUNT_KIND.percentage,
  discountAmount: coupon.discount?.amount ?? null,
  discountPercentage: coupon.discountPercentage ?? null,
  ...appliesToModeFromLists({
    productCount: coupon.validForSalesItems.length,
    tagCount: coupon.validForSalesItemTags.length
  }),
  salesItemIds: coupon.validForSalesItems.map((item) => item.id),
  salesItemTagIds: coupon.validForSalesItemTags.map((tag) => tag.id)
});

export const EditCouponDiscountSheet = ({ open, onClose, coupon }: Props) => {
  if (!open) {
    return null;
  }

  return <EditCouponDiscountForm key={coupon.id} open={open} onClose={onClose} coupon={coupon} />;
};

type FormProps = {
  open: boolean;
  onClose: () => void;
  coupon: CouponRecord;
};

const EditCouponDiscountForm = ({ open, onClose, coupon }: FormProps) => {
  const { serviceOrganizationId, currency: scopeCurrency, useStaffCouponWrite } = usePromotionBrandScope();
  const currency: Currency | null = coupon.discount?.currency ?? scopeCurrency;

  const { mutateAsync: patchPartnerCoupon } = noddiAsync.usePatch({
    type: URLKeys.patchServiceOrganizationCoupon,
    queryConfig: {
      onSuccess: () => {
        void invalidateAfterCouponMutation({ couponId: coupon.id, serviceOrganizationId });
        onClose();
      }
    }
  });

  const { mutateAsync: patchAdminCoupon } = noddiAsync.usePatch({
    type: URLKeys.patchCoupon,
    queryConfig: {
      onSuccess: () => {
        void invalidateAfterCouponMutation({ couponId: coupon.id });
        onClose();
      }
    }
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(coupon),
    validationLogic: revalidateLogic(),
    validators: { onDynamic: validationSchema, onSubmit: validationSchema },
    onSubmit: async ({ value }) => {
      if (value.discountKind === DISCOUNT_KIND.amount && currency == null) {
        return;
      }

      const ids = appliesToIdsForSubmit({
        mode: value,
        productIds: value.salesItemIds,
        tagIds: value.salesItemTagIds
      });
      const payload = {
        discount:
          value.discountKind === DISCOUNT_KIND.amount && value.discountAmount !== null && currency != null
            ? { amount: value.discountAmount, currency }
            : null,
        discountPercentage: value.discountKind === DISCOUNT_KIND.percentage ? value.discountPercentage : null,
        validForSalesItemIds: ids.productIds,
        validForSalesItemTagIds: ids.tagIds
      };
      if (!useStaffCouponWrite && serviceOrganizationId != null) {
        await patchPartnerCoupon({ serviceOrganizationId, couponId: coupon.id, ...payload });
        return;
      }
      await patchAdminCoupon({ couponId: coupon.id, ...payload });
    }
  });

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit discount'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Subscribe selector={(state) => state.values.discountKind}>
            {(discountKind) => (
              <DiscountKindRadioGroup
                value={discountKind}
                onValueChange={(value) => form.setFieldValue('discountKind', value)}
                valueInput={
                  discountKind === DISCOUNT_KIND.amount ? (
                    currency != null ? (
                      <form.AppField name='discountAmount'>
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack field render prop */}
                        {(field: any) => <field.Input {...discountAmountInputProps({ currency })} />}
                      </form.AppField>
                    ) : (
                      <p className='text-sm text-muted-foreground'>
                        Select a department with a currency to set an amount discount.
                      </p>
                    )
                  ) : (
                    <form.AppField name='discountPercentage'>
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack field render prop */}
                      {(field: any) => <field.Input {...discountPercentageInputProps()} />}
                    </form.AppField>
                  )
                }
              />
            )}
          </form.Subscribe>
          <form.Subscribe
            selector={(state) => ({
              appliesToAllProducts: state.values.appliesToAllProducts,
              filterByProducts: state.values.filterByProducts,
              filterByTags: state.values.filterByTags
            })}
          >
            {(mode) => (
              <AppliesToModeFields
                values={mode}
                onChange={(next) => {
                  form.setFieldValue('appliesToAllProducts', next.appliesToAllProducts);
                  form.setFieldValue('filterByProducts', next.filterByProducts);
                  form.setFieldValue('filterByTags', next.filterByTags);
                  if (next.appliesToAllProducts) {
                    form.setFieldValue('salesItemIds', []);
                    form.setFieldValue('salesItemTagIds', []);
                  }
                  if (!next.filterByProducts) {
                    form.setFieldValue('salesItemIds', []);
                  }
                  if (!next.filterByTags) {
                    form.setFieldValue('salesItemTagIds', []);
                  }
                }}
                productsPicker={
                  <form.AppField name='salesItemIds'>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack field render prop */}
                    {(field: any) => (
                      <CouponSalesItemsPicker
                        salesItemIds={field.state.value}
                        onSalesItemIdsChange={(next) => field.handleChange(next)}
                      />
                    )}
                  </form.AppField>
                }
                tagsPicker={
                  <form.AppField name='salesItemTagIds'>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack field render prop */}
                    {(field: any) => (
                      <CampaignDiscountSalesItemTagsPicker
                        salesItemTagIds={field.state.value}
                        onSalesItemTagIdsChange={(next) => field.handleChange(next)}
                      />
                    )}
                  </form.AppField>
                }
              />
            )}
          </form.Subscribe>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
